import React, { useEffect, useState, useCallback, useRef } from "react";
import { message, ConfigProvider } from "antd";
import moment from "moment";
import { Routes, Route, useLocation } from "react-router-dom";
import ForgotPassword from "./Components/Screens/Auth/ForgotPassword";
import Login from "./Components/Screens/Auth/Login";
import ResetPassword from "./Components/Screens/Auth/ResetPassword";
import InitialResetPassword from "./Components/Screens/Auth/InitialResetPassword";
import LoginService from "./Services/LoginService";
import Career from "./Components/Screens/Career/Career";
import Main from "./Main/Main";
import PageTitle from "./Components/Layouts/PageTitle";
import { ImagePreviewHost } from "./Components/Reuseable/ImagePreview";
import secureLocalStorage from "react-secure-storage";
import EvaluationService from "./Services/EvaluationService";
import UserServices from "./Services/UserService";
import EvaluationPopup from "./Components/Layouts/EvaluationPopup";
import DataParser from "./utlits/Dataparser";
import SocketService from "./Services/SocketService";
import CompanyService from "./Services/CompanyService";
import Welcome from "./Components/Screens/Welcome/Welcome";
import PrivacyPolicy from "./Components/Screens/Auth/PrivacyPolicy";
import TermsOfService from "./Components/Screens/Auth/TermsOfService";

// Ensure antd message toasts always render above all custom modals (rejection modal is at z-index 1000100)
message.config({ zIndex: 1100000 });

const App = () => {
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [showGlobalConfirm, setShowGlobalConfirm] = useState(false);
  const pendingModalToClose = useRef(null);
  const location = useLocation();

  const loggedUser = JSON.parse(secureLocalStorage.getItem("adminInfo"));

  const headId = loggedUser?.user?.userdata?.id;
  const companyId = loggedUser?.user?.userdata?.company_id;
  const isHead = loggedUser?.user?.userdata?.is_head;

  // ✅ Pending evaluations check logic
  const checkPendingEvaluations = useCallback(async () => {
    if (!headId || !companyId || isHead !== "yes") {
      return;
    }

    try {
      const res = await EvaluationService.getPendingEvaluations(headId, companyId);
      const data = res.data;

      if (!data || !data.hasPending) {
        setShowPopup(false);
        return;
      }

      const { screenBlocked, alreadySkippedToday } = data;

      // Read the live path after the await so that in-flight calls that resolve
      // after the user has already navigated to /evaluation don't re-show the
      // popup. Segment matching prevents /myevaluation from being treated as
      // the evaluate-team page ("myevaluation" !== "evaluation").
      const isOnEvaluationPage = window.location.pathname.split('/').includes("evaluation");

      if ((screenBlocked || !alreadySkippedToday) && !isOnEvaluationPage) {
        setPopupData(data);
        setShowPopup(true);
      } else {
        setShowPopup(false);
      }
    } catch (err) {
      console.error("Pending evaluation check failed:", err);
    }
  }, [headId, companyId, isHead]);

  // ✅ Enforce Blocker when navigating
  useEffect(() => {
    if (!popupData?.screenBlocked) return;

    const publicPaths = ["/", "/career", "/forgot-password", "/reset-password", "/initial-reset-password", "/unauthorized"];
    const isPublicPath = publicPaths.some(path => location.pathname === path || location.pathname.startsWith("/reset-password/"));
    const isOnEvaluationPage = location.pathname.split('/').includes("evaluation");

    if (isOnEvaluationPage || isPublicPath) {
      setShowPopup(false);
    } else {
      // Re-fetch fresh state — evaluations may have just been completed on the previous page.
      // Using stale popupData here would incorrectly re-show the blocker after all evals are done.
      checkPendingEvaluations();
    }
  }, [location.pathname, popupData?.screenBlocked, checkPendingEvaluations]);

  // Prevent Bootstrap's modal focus trap from stealing focus when the user
  // clicks inside an Ant Design picker/select dropdown portaled to document.body.
  // Bootstrap's focus trap fires a bubble-phase 'focusin' listener; running ours
  // in capture phase (fires first) and stopping propagation blocks Bootstrap before
  // it can call modal.focus() — which would blur the picker and close its dropdown.
  useEffect(() => {
    // .keep-focus-popup: our own popups portaled to body (e.g. the Task Delayed popup, which can open over a modal)
    const ANT_SELECTOR = '.ant-picker-dropdown, .ant-select-dropdown, .ant-picker-panel-container, .ant-popover, .keep-focus-popup';
    const preventFocusTrap = (e) => {
      if (e.target.closest(ANT_SELECTOR)) e.stopImmediatePropagation();
    };
    const preventPickerBlur = (e) => {
      if (e.target.closest('.ant-picker-dropdown, .ant-picker-panel-container')) {
        // Prevent clicking inside date/time picker panel from stealing focus from input and closing dropdown
        e.preventDefault();
      }
    };
    document.addEventListener('focusin',  preventFocusTrap,     true);
    document.addEventListener('mousedown', preventPickerBlur);
    return () => {
      document.removeEventListener('focusin',  preventFocusTrap,     true);
      document.removeEventListener('mousedown', preventPickerBlur);
    };
  }, []);

  // Close Bootstrap modals when clicking outside modal-content (including static-backdrop modals).
  const handleGlobalConfirmClose = () => {
    setShowGlobalConfirm(false);
    if (pendingModalToClose.current && window.bootstrap) {
      (window.bootstrap.Modal.getInstance(pendingModalToClose.current) || new window.bootstrap.Modal(pendingModalToClose.current)).hide();
      pendingModalToClose.current = null;
    }
  };

  useEffect(() => {
    const handleModalBackdropClick = (e) => {
      const modalEl = e.target.closest('.modal.show');
      if (!modalEl) return;
      if (e.target.closest('.modal-content') || e.target.closest('.ant-picker-dropdown, .ant-select-dropdown, .ant-picker-panel-container, .ant-popover')) return;
      const bsModal = window.bootstrap?.Modal?.getInstance(modalEl);
      if (bsModal) bsModal.hide();
    };
    document.addEventListener('click', handleModalBackdropClick);
    return () => document.removeEventListener('click', handleModalBackdropClick);
  }, []);

  // 🔁 Force update listener
  useEffect(() => {
    const handlePermissionsUpdated = () => {
      // Recalculate loggedUser from storage
      const updatedUser = JSON.parse(secureLocalStorage.getItem("adminInfo"));
      setForceUpdate((prev) => prev + 1);
    };

    window.addEventListener("permissionsUpdated", handlePermissionsUpdated);
    window.addEventListener("companyModulesUpdated", handlePermissionsUpdated);
    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
      window.removeEventListener("companyModulesUpdated", handlePermissionsUpdated);
    };
  }, []);

  // 🕒 Proactive Session Expiry Check (Idle Timeout)
  useEffect(() => {
    // 1. Initialize Last Active Time
    if (!localStorage.getItem("lastActiveTime")) {
      localStorage.setItem("lastActiveTime", new Date().getTime().toString());
    }

    // 2. Activity Listener (Throttled)
    const updateLastActive = () => {
      localStorage.setItem("lastActiveTime", new Date().getTime().toString());
    };

    const throttle = (func, limit) => {
      let inThrottle;
      return function () {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => (inThrottle = false), limit);
        }
      };
    };

    const throttledUpdate = throttle(updateLastActive, 5000); // 🟢 Update max once every 5s

    const handleUserActivity = () => {
      // If user isn't logged in, don't bother tracking
      if (!secureLocalStorage.getItem("adminInfo")) return;
      throttledUpdate();
    };

    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("keypress", handleUserActivity);
    window.addEventListener("click", handleUserActivity);
    window.addEventListener("scroll", handleUserActivity);

    // 3. Periodic Check
    const checkSession = () => {
      const adminInfo = JSON.parse(secureLocalStorage.getItem("adminInfo"));
      if (!adminInfo) return; // Don't check if not logged in

      const lastActive = localStorage.getItem("lastActiveTime");
      // Default to 6 hours if timeout mins not set
      const timeoutMins = JSON.parse(secureLocalStorage.getItem("sessionTimeoutMins")) || 360;

      if (lastActive && timeoutMins) {
        const currentTime = new Date().getTime();
        const allowedIdleTime = Number(timeoutMins) * 60 * 1000;
        const timeSinceLastActive = currentTime - Number(lastActive);

        if (timeSinceLastActive > allowedIdleTime) {
          console.log("🕒 Session expired due to inactivity. Logging out...");
          localStorage.removeItem("lastActiveTime");
          LoginService.logout();
          window.location.href = "/";
        }
      }
    };

    // Check every 30 seconds
    const interval = setInterval(checkSession, 30000);
    checkSession(); // Also check on mount

    return () => {
      clearInterval(interval);
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keypress", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
    };
  }, []);

  // 🟢 Socket Listener for Permissions
  // 🟢 Socket Listener for Permissions
  useEffect(() => {
    SocketService.connect();

    const handlePermissionsUpdate = (data) => {
      // Check if the update is for the current user's role and company
      // Relaxed check for company_id to handle string/number mismatch
      if (String(data.company_id) === String(companyId) && data.action === "update_permissions") {
        const currentUserRole = loggedUser?.user?.userdata?.role;
        if (String(data.roleId) === String(currentUserRole)) {
          console.log("🔔 Permissions updated via Socket:", data.permissions);
          // toast.info("Your permissions have been updated!"); // Optional: Visual feedback

          if (loggedUser && loggedUser.user) {
            updateLocalPermissions(data.permissions);
          }
        }
      }
    };

    const handleEvaluationUpdate = (data) => {
      if (Number(data.company_id) === Number(companyId)) {
        console.log("🔔 Evaluation update received via Socket.");
        checkPendingEvaluations();
      }
    };

    SocketService.on("role_update", handlePermissionsUpdate);

    const handleSessionUpdate = (data) => {
      if (Number(data.company_id) === Number(companyId)) {
        if (data.action === "update" || data.action === "create") {
          console.log("🔔 Session timeout updated via Socket:", data.data.timeout_minutes);
          secureLocalStorage.setItem("sessionTimeoutMins", JSON.stringify(data.data.timeout_minutes));
        } else if (data.action === "status_update") {
          // If deactivated, revert to default 6h (360 mins)
          const newTimeout = data.data.status === 1 ? data.data.timeout_minutes : 360;
          secureLocalStorage.setItem("sessionTimeoutMins", JSON.stringify(newTimeout));
        }
      }
    };

    SocketService.on("session_settings_update", handleSessionUpdate);
    SocketService.on("evaluation_update", handleEvaluationUpdate);

    // 🟢 Live module/package entitlement refresh — a company's access to a
    // module can change from the SaaS admin side (module drafted, removed
    // from a package, or the company's package itself changed) without that
    // company's already-open session ever refreshing or re-logging in.
    const handleModulesUpdate = () => refreshCompanyModules();
    const handlePackagesUpdate = () => refreshCompanyModules();
    const handleCompaniesUpdate = (data) => {
      if (String(data?.data?.id) === String(companyId)) refreshCompanyModules();
    };

    SocketService.on("modules_update", handleModulesUpdate);
    SocketService.on("packages_update", handlePackagesUpdate);
    SocketService.on("companies_update", handleCompaniesUpdate);

    return () => {
      SocketService.off("role_update", handlePermissionsUpdate);
      SocketService.off("session_settings_update", handleSessionUpdate);
      SocketService.off("evaluation_update", handleEvaluationUpdate);
      SocketService.off("modules_update", handleModulesUpdate);
      SocketService.off("packages_update", handlePackagesUpdate);
      SocketService.off("companies_update", handleCompaniesUpdate);
    };
  }, [loggedUser, companyId, checkPendingEvaluations]);

  const refreshCompanyModules = async () => {
    if (!companyId) return; // SaaS admin / no company — nothing to refresh
    try {
      const res = await CompanyService.getCompanyModules(companyId);
      if (res?.Good) {
        let adminInfo = JSON.parse(secureLocalStorage.getItem("adminInfo"));
        if (adminInfo?.user?.company) {
          const newModules = res.data?.modules || [];
          // Any SaaS module/package edit is announced to every company, and the event below remounts the whole
          // app: only go on when this company's modules really changed
          if (JSON.stringify(adminInfo.user.company.modules) === JSON.stringify(newModules)) return;
          adminInfo.user.company.modules = newModules;
          secureLocalStorage.setItem("adminInfo", JSON.stringify(adminInfo));
          console.log("🔔 Company modules updated via Socket:", res.data?.modules);
          window.dispatchEvent(new Event("companyModulesUpdated"));
        }
      }
    } catch (e) {
      console.error("Failed to refresh company modules:", e);
    }
  };

  const updateLocalPermissions = (newPermissions) => {
    try {
      // Update local storage
      let adminInfo = JSON.parse(secureLocalStorage.getItem("adminInfo"));
      if (adminInfo) {
        if (adminInfo && adminInfo.user && adminInfo.user.userdata) {
          adminInfo.user.userdata.user_permissions = newPermissions;
          secureLocalStorage.setItem("adminInfo", JSON.stringify(adminInfo));

          console.log("Permissions updated in local storage:", newPermissions);

          // Trigger force update
          window.dispatchEvent(new Event("permissionsUpdated"));
        }
      }
    } catch (e) {
      console.error("Failed to update permissions:", e);
    }
  };

  // Trigger check on mount
  useEffect(() => {
    checkPendingEvaluations();
  }, [checkPendingEvaluations]);

  return (
    <ConfigProvider getPopupContainer={() => document.body}>
    <>
      {/* Before the routes so each page's own <Helmet> title overrides it */}
      <PageTitle />
      <ImagePreviewHost />
      <Routes>
        <Route exact path="/" element={<Login />} />
        <Route exact path="/home" element={<Welcome />} />
        <Route exact path="/career" element={<Career />} />
        <Route exact path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route exact path="/initial-reset-password" element={<InitialResetPassword />} />
        <Route exact path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route exact path="/terms" element={<TermsOfService />} />
        <Route exact path="/unauthorized" element={
          <div style={{ textAlign: 'center', padding: '100px' }}>
            <h1>Unauthorized Access</h1>
            <p>You do not have permission to view this page.</p>
            <button onClick={() => window.location.href = "/"}>Back to Login</button>
          </div>
        } />
      </Routes>

      <Main key={forceUpdate} />

      {showPopup && popupData && (
        <EvaluationPopup
          data={popupData}
          onClose={() => setShowPopup(false)}
        />
      )}

      {/* Discard Changes confirmation dialog -- commented out, re-enable by uncommenting the block below
      {showGlobalConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100030, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-content" style={{ maxWidth: '380px', width: '90%', borderRadius: '12px', border: 'none', padding: '8px 4px' }}>
            <div className="modal-header border-0 pb-1">
              <h5 className="modal-title" style={{ fontWeight: 700, fontSize: '16px' }}>Discard Changes?</h5>
            </div>
            <div className="modal-body py-2">
              <p style={{ margin: 0, color: '#555', fontSize: '14px' }}>You have unsaved changes. Are you sure you want to close?</p>
            </div>
            <div className="modal-footer border-0 pt-2">
              <button type="button" className="btn btn-light btn-sm" style={{ borderRadius: '8px', padding: '6px 18px' }} onClick={() => setShowGlobalConfirm(false)}>Stay</button>
              <button type="button" className="btn btn-danger btn-sm" style={{ borderRadius: '8px', padding: '6px 18px' }} onClick={handleGlobalConfirmClose}>Close Anyway</button>
            </div>
          </div>
        </div>
      )}
      */}
    </>
    </ConfigProvider>
  );
};

export default App;
